import React, { useState, useRef, useEffect } from 'react';
import { useJurisdictionSearch } from '@/hooks/useJurisdictionSearch';
import { Place } from '@/contexts/PlaceNamesContext';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/shared/popover';

interface AddPeerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddJurisdiction: (place: Place) => void;
  children: React.ReactNode;
}

export const AddPeerDialog: React.FC<AddPeerDialogProps> = ({
  open,
  onOpenChange,
  onAddJurisdiction,
  children,
}) => {
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    searchInputValue,
    setSearchInputValue,
    placeOptions = [],
    isSearching,
    isDebouncing,
    searchError,
    clearResults,
  } = useJurisdictionSearch(showAutocomplete);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInputValue(value);
    setShowAutocomplete(true);
  };

  const handleInputFocus = () => {
    setShowAutocomplete(true);
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setShowAutocomplete(false);
      clearResults();
    }, 150);
  };

  const handleSelectItem = (place: Place & { displayName: string }) => {
    // Use the original place data without the displayName
    const { displayName, ...originalPlace } = place;
    onAddJurisdiction(originalPlace);
    setSearchInputValue('');
    setShowAutocomplete(false);
    clearResults();
    onOpenChange(false);
  };

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSearchInputValue('');
      setShowAutocomplete(false);
      clearResults();
    }
  }, [open]);

  // Focus input when dialog opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <div>
          {children}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="center">
        <div>
          <div className="relative">
            <label htmlFor="peer-search" className="sr-only">
              Search for jurisdiction
            </label>
            <div className="relative">
              <input
                ref={inputRef}
                id="peer-search"
                type="text"
                value={searchInputValue}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                autoComplete="off"
                autoCapitalize="off"
                spellCheck="false"
                aria-autocomplete="list"
                className="text-[#CA0079] bg-white w-full px-4 py-3 rounded-[11px] border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#CA0079] focus:border-[#CA0079] text-base pl-12 font-geist"
              />
              
              {/* Persistent magnifying glass icon */}
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 pointer-events-none flex items-center h-full">
                <span className="material-symbols-outlined text-[#CA0079] text-xl opacity-50 leading-none">
                  search
                </span>
              </div>

              {/* Placeholder text */}
              {!searchInputValue && (
                <div className="absolute left-12 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <span className="text-[#CA0079] text-base font-medium uppercase opacity-50 font-geist">
                    Select a jurisdiction
                  </span>
                </div>
              )}
            </div>

            {/* Autocomplete dropdown */}
            {showAutocomplete && placeOptions && placeOptions.length > 0 && (
              <div 
                ref={dropdownRef}
                className="absolute top-full left-0 right-0 mt-2 bg-white rounded-[11px] shadow-lg border border-gray-200 z-50 max-h-60 overflow-y-auto"
              >
                {placeOptions.map((place) => (
                  <button
                    key={place.place_fips}
                    onClick={() => handleSelectItem(place)}
                    className="w-full text-left px-4 py-3 text-[#CA0079] hover:bg-gray-50 transition-colors text-base first:rounded-t-[11px] last:rounded-b-[11px] font-geist"
                  >
                    {place.displayName}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Loading state */}
          {isDebouncing && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-[11px] shadow-lg border border-gray-200 z-50 p-4 text-center text-[#CA0079]">
              Searching...
            </div>
          )}
          
          {/* Error state */}
          {searchError && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-[11px] shadow-lg border border-red-200 z-50 p-4 text-center text-red-500">
              {searchError}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
